import { ActionButtonDefinitionDTO, FlowAction, Res } from 'core/api';
import { SessionProvider } from 'core/infrastructure';
import { Message } from 'core/ui-next/modal';
import { Command } from 'pos/common';
import { UserActivityMonitor } from 'pos/services';
import { ActionButtonDefinitionFactory } from 'pos/ui';
import * as SalesStore from '../SalesStore';
import { initSale } from './initSale';
import { SaleModuleNextAction } from 'pos/modules/helpers/SaleModuleNextAction';
import { PrintBalanceNextAction } from 'pos/modules/helpers/PrintBalanceNextAction';

type Params = {
	hasCashControlNotification: () => boolean,
	hasTakeOutOrdersNotification: () => boolean,
	applyFlowAction: (action: FlowAction) => Promise<void>,

};

export function useSelectSaleLocationCallbacks(params: Params): ActionButtonDefinitionFactory.SaleLocationActionCommands {

	const closeDay = async () => {
		await SalesStore.release();
		const result = await Command.execute('closeDay');
		if (result.ok)
			await params.applyFlowAction(result.value);
	};

	const applySaleAction = async (action: SaleModuleNextAction) => {
		if (action.type === 'loadTicket') {
			await SalesStore.loadTicketById(action.ticketId);
			const result = await initSale();
			if (result.ok)
				await params.applyFlowAction(result.value);
		}
	};

	return {
		hasCashControlNotification: params.hasCashControlNotification,
		hasTakeOutOrdersNotification: params.hasTakeOutOrdersNotification,
		executeDirectAction: async (action: ActionButtonDefinitionDTO & { imageSrc: string }) => {
			await UserActivityMonitor.registerWhile(async () => {
				return await Command.execute('runDirectAction', {
					actionId: action.ActionId,
					buttonText: action.Text,
					introducedValue: null,
					selectedLine: null,
					imageSrc: action.imageSrc,
					externalActionArgs: {
						posId: SessionProvider.getCurrent().PosId,
						userId: SessionProvider.getCurrent().UserId,
						ticketId: null,
						ticketGlobalId: null,
						ticketLineIndex: null
					}
				});
			});
		},
		setProductAvailability: async () => {
			await Command.execute('setProductAvailability');
		},
		registerInputCashOperation: async () => {
			await Command.execute('registerInputCahsOperation');
		},
		registerOutputCashOperation: async () => {
			await Command.execute('registerOutputCashOperation');
		},
		openCashDrawer: async () => {
			const result = await Command.execute('openCashDrawer');
			if (result.ok)
				await params.applyFlowAction(result.value);
		},
		printPartialBalance: async () => {
			await Command.execute('printPartialBalance');
		},
		printTotalBalance: async () => {
			const result = await Command.execute('printTotalBalance');
			if (!result.ok)
				return;

			const confirmationResult = await Message.confirm(Res.Msg.MSG_CONFIRM_CLOSE_DAY_AFTER_PRINT_BALANCE());
			if (!confirmationResult.ok)
				return;

			await closeDay();
		},
		closeDay: async () => {
			await closeDay();
		},
		findInvoice: async () => {
			const result = await Command.execute('showInvoices');
			if (result.ok) {
				await applySaleAction(result.value);
			}
		},
		configurePriceList: async () => {
			await Command.execute('configurePriceList');
		},
		showDeliveryNotes: async () => {
			const result = await Command.execute('showDeliveryNotes');
			if (result.ok)
				await applySaleAction(result.value);
		},
		showSalesOrders: async () => {
			const result = await Command.execute('showSalesOrders');
			if (result.ok)
				await applySaleAction(result.value);
		},
		showProductsInPendingSalesOrder: async () => {
			await Command.execute('showSaleOrdersPreparations', []);
		},
		showCashControlStatus: async () => {
			await Command.execute('showCashControlStatus');
		},
		giveChangeFromCashControl: async () => {
			await Command.execute('giveChangeFromCashControl');
		},
		showSystemStatus: async () => {
			const result = await Command.execute('showSystemStatus');
			if (result.ok && result.value === PrintBalanceNextAction.CloseDay) {
				await closeDay();
			}
		},
		configureMenu: async () => {
			await Command.execute('configureMenu');
		},
		showAppointments: async () => {
			const result = await Command.execute('showAppointments');
			if (result.ok)
				await applySaleAction(result.value);
		},
		printTrainingSession: async () => {
			await Command.execute('printTrainingSession');
		},
		runAdmin: async () => {
			void Command.execute('runAdmin');
		},
		resetPreparationCounter: async () => {
			await Command.execute('resetPreparationCounter');
		},
		selectOpenTicket: async () => {
			const result = await Command.execute('selectOpenTicket');
			if (!result.ok)
				return;

			const saleResult = await initSale();
			if (saleResult.ok)
				await params.applyFlowAction(saleResult.value);
		},
		minimize: async () => {
			await Command.execute('minimizeWindow');
		},
		doClocking: async () => {
			await Command.execute('doClocking');
		},
		showTakeOutOrders: async () => {
			const result = await Command.execute('showTakeOutOrders');
			if (result.ok)
				await applySaleAction(result.value);
		},
		configureSmartMenuOrderComms: async () => {
			await Command.execute('configureSmartMenuOrderComms');
		},
		showDeliveryPersonStatuses: async () => {
			await Command.execute('showDeliveryPersonStatuses');
		},
		showCardProcessorAdvancedOptions: async () => {
			await Command.execute('showCardProcessorAdvancedOptions');
		}
	};
}

